import { useEffect, useReducer, useCallback } from 'react';
import { Info, Send, Loader2, Sparkles, Eye, BookOpen, PlusCircle, RefreshCw } from 'lucide-react';
import AnnotatableText from './AnnotatableText';
import AnalysisForm from './AnalysisForm';
import { useApi } from '../../hooks/useApi';

interface Annotation {
  start: number;
  end: number;
  categories: string[];
  note?: string;
}

interface Category {
  id: string;
  group_name: string;
  name: string;
  label: string;
  description: string | null;
  color: string | null;
}

interface Exercise {
  id: string;
  title: string;
  ai_text: string;
  ai_model: string | null;
  ai_prompt: string | null;
  context_notes: string | null;
  min_annotations: number;
  difficulty: string;
}

interface Submission {
  id: string;
  annotations: Annotation[];
  claims: string | null;
  argument: string | null;
  theory: string | null;
  completed_at: string | null;
}

interface State {
  phase: 'annotating' | 'analyzing' | 'submitted' | 'feedback' | 'refining';
  annotations: Annotation[];
  claims: string;
  argument: string;
  theory: string;
  submissionId: string | null;
  aiFeedback: any | null;
  exemplar: any | null;
  saving: boolean;
  loadingFeedback: boolean;
  addedFromFeedback: number[]; // Track which missed patterns were added
}

type Action =
  | { type: 'ADD_ANNOTATION'; payload: Annotation }
  | { type: 'REMOVE_ANNOTATION'; payload: number }
  | { type: 'SET_CLAIMS'; payload: string }
  | { type: 'SET_ARGUMENT'; payload: string }
  | { type: 'SET_THEORY'; payload: string }
  | { type: 'SET_PHASE'; payload: State['phase'] }
  | { type: 'SET_SUBMISSION_ID'; payload: string }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_AI_FEEDBACK'; payload: any }
  | { type: 'SET_EXEMPLAR'; payload: any }
  | { type: 'SET_LOADING_FEEDBACK'; payload: boolean }
  | { type: 'LOAD_SUBMISSION'; payload: Submission }
  | { type: 'MARK_PATTERN_ADDED'; payload: number }
  | { type: 'START_REFINING' };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD_ANNOTATION':
      return { ...state, annotations: [...state.annotations, action.payload] };
    case 'REMOVE_ANNOTATION':
      return { ...state, annotations: state.annotations.filter((_, i) => i !== action.payload) };
    case 'SET_CLAIMS':
      return { ...state, claims: action.payload };
    case 'SET_ARGUMENT':
      return { ...state, argument: action.payload };
    case 'SET_THEORY':
      return { ...state, theory: action.payload };
    case 'SET_PHASE':
      return { ...state, phase: action.payload };
    case 'SET_SUBMISSION_ID':
      return { ...state, submissionId: action.payload };
    case 'SET_SAVING':
      return { ...state, saving: action.payload };
    case 'SET_AI_FEEDBACK':
      return { ...state, aiFeedback: action.payload, loadingFeedback: false };
    case 'SET_EXEMPLAR':
      return { ...state, exemplar: action.payload };
    case 'SET_LOADING_FEEDBACK':
      return { ...state, loadingFeedback: action.payload };
    case 'LOAD_SUBMISSION':
      return {
        ...state,
        annotations: action.payload.annotations,
        claims: action.payload.claims || '',
        argument: action.payload.argument || '',
        theory: action.payload.theory || '',
        submissionId: action.payload.id,
        phase: action.payload.completed_at ? 'submitted' : 'annotating'
      };
    case 'MARK_PATTERN_ADDED':
      return {
        ...state,
        addedFromFeedback: [...state.addedFromFeedback, action.payload]
      };
    case 'START_REFINING':
      return {
        ...state,
        phase: 'refining'
      };
    default:
      return state;
  }
}

interface CloseReadingExerciseProps {
  exercise: Exercise;
  submission?: Submission | null;
  categories: Record<string, Category[]>;
  mode?: 'NO_TOOL' | 'AUDIT' | 'OPEN_AI';
}

export function CloseReadingExercise({
  exercise,
  submission,
  categories,
  mode = 'AUDIT'
}: CloseReadingExerciseProps) {
  const { fetchApi } = useApi();

  const [state, dispatch] = useReducer(reducer, {
    phase: 'annotating',
    annotations: [],
    claims: '',
    argument: '',
    theory: '',
    submissionId: null,
    aiFeedback: null,
    exemplar: null,
    saving: false,
    loadingFeedback: false,
    addedFromFeedback: []
  });

  // Load existing submission
  useEffect(() => {
    if (submission) {
      dispatch({ type: 'LOAD_SUBMISSION', payload: submission });
    }
  }, [submission]);

  const handleSubmit = async () => {
    dispatch({ type: 'SET_SAVING', payload: true });
    try {
      const response = await fetchApi<{ id: string }>('/api/close-reading/submissions', {
        method: 'POST',
        body: JSON.stringify({
          exercise_id: exercise.id,
          annotations: state.annotations,
          claims: state.claims,
          argument: state.argument,
          theory: state.theory,
          mode
        })
      });
      dispatch({ type: 'SET_SUBMISSION_ID', payload: response.id });
      dispatch({ type: 'SET_PHASE', payload: 'submitted' });
    } catch (err) {
      console.error('Failed to submit:', err);
    } finally {
      dispatch({ type: 'SET_SAVING', payload: false });
    }
  };

  const handleGetFeedback = async () => {
    if (!state.submissionId) return;
    dispatch({ type: 'SET_LOADING_FEEDBACK', payload: true });
    try {
      const response = await fetchApi<{ feedback: any }>(`/api/close-reading/submissions/${state.submissionId}/ai-feedback`, {
        method: 'POST'
      });
      dispatch({ type: 'SET_AI_FEEDBACK', payload: response.feedback });
      dispatch({ type: 'SET_PHASE', payload: 'feedback' });
    } catch (err) {
      console.error('Failed to get feedback:', err);
      dispatch({ type: 'SET_LOADING_FEEDBACK', payload: false });
    }
  };

  const handleGetExemplar = async () => {
    if (!state.submissionId) return;
    try {
      const response = await fetchApi<{ exemplar: any }>(`/api/close-reading/submissions/${state.submissionId}/exemplar`);
      dispatch({ type: 'SET_EXEMPLAR', payload: response.exemplar });
    } catch (err) {
      console.error('Failed to get exemplar:', err);
    }
  };

  // Find category by name from the grouped categories
  const findCategoryByName = useCallback((categoryName: string): string | null => {
    for (const group of Object.values(categories)) {
      for (const cat of group) {
        if (cat.label.toLowerCase() === categoryName.toLowerCase() ||
            cat.name.toLowerCase() === categoryName.toLowerCase()) {
          return cat.id;
        }
      }
    }
    return null;
  }, [categories]);

  // Add annotation from AI feedback missed pattern
  const handleAddFromFeedback = useCallback((patternIndex: number, passage: string, suggestedCategory: string) => {
    // Find the passage in the AI text
    const text = exercise.ai_text;
    const start = text.indexOf(passage);

    if (start === -1) {
      // Try partial match (first 50 chars) if exact match fails
      const partialPassage = passage.slice(0, 50);
      const partialStart = text.indexOf(partialPassage);
      if (partialStart === -1) {
        console.warn('Could not find passage in text:', passage);
        return;
      }
    }

    const end = start + passage.length;

    // Find the category ID
    const categoryId = findCategoryByName(suggestedCategory);

    // Create annotation
    const annotation: Annotation = {
      start,
      end,
      categories: categoryId ? [categoryId] : [],
      note: `Added from AI feedback: ${suggestedCategory}`
    };

    dispatch({ type: 'ADD_ANNOTATION', payload: annotation });
    dispatch({ type: 'MARK_PATTERN_ADDED', payload: patternIndex });

    // Switch to refining mode if not already there
    if (state.phase !== 'refining') {
      dispatch({ type: 'START_REFINING' });
    }
  }, [exercise.ai_text, findCategoryByName, state.phase]);

  const handleStartRefining = useCallback(() => {
    dispatch({ type: 'START_REFINING' });
  }, []);

  const isComplete = state.claims.length >= 100 && state.argument.length >= 200 && state.theory.length >= 150;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h2 className="text-xl font-semibold text-gray-100 mb-2">{exercise.title}</h2>
        <div className="flex items-center gap-4 text-sm text-gray-400">
          {exercise.ai_model && (
            <span className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              {exercise.ai_model}
            </span>
          )}
          <span className={`px-2 py-0.5 rounded text-xs ${
            exercise.difficulty === 'beginner' ? 'bg-green-600/20 text-green-400' :
            exercise.difficulty === 'advanced' ? 'bg-red-600/20 text-red-400' :
            'bg-yellow-600/20 text-yellow-400'
          }`}>
            {exercise.difficulty}
          </span>
        </div>
      </div>

      {/* Scene Setting - Context */}
      {(exercise.ai_prompt || exercise.context_notes) && (
        <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-300 mb-1">Scene Setting</h3>
              {exercise.ai_prompt && (
                <p className="text-sm text-gray-300 mb-2">
                  <span className="text-gray-500">Prompt:</span> "{exercise.ai_prompt}"
                </p>
              )}
              {exercise.context_notes && (
                <p className="text-sm text-gray-400">{exercise.context_notes}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Annotatable Text */}
        <div>
          <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
            AI Response
            <span className="text-xs bg-gray-700 px-2 py-0.5 rounded">
              {state.annotations.length}/{exercise.min_annotations}+ annotations
            </span>
          </h3>
          <AnnotatableText
            text={exercise.ai_text}
            annotations={state.annotations}
            categories={categories}
            onAnnotationAdd={(ann) => dispatch({ type: 'ADD_ANNOTATION', payload: ann })}
            onAnnotationRemove={(idx) => dispatch({ type: 'REMOVE_ANNOTATION', payload: idx })}
            readonly={state.phase === 'submitted'}
          />
        </div>

        {/* Right: Analysis Form or Results */}
        <div>
          {state.phase === 'annotating' || state.phase === 'analyzing' ? (
            <>
              <h3 className="text-sm font-medium text-gray-400 mb-2">Your Analysis</h3>
              <AnalysisForm
                claims={state.claims}
                argument={state.argument}
                theory={state.theory}
                onClaimsChange={(v) => dispatch({ type: 'SET_CLAIMS', payload: v })}
                onArgumentChange={(v) => dispatch({ type: 'SET_ARGUMENT', payload: v })}
                onTheoryChange={(v) => dispatch({ type: 'SET_THEORY', payload: v })}
                minAnnotations={exercise.min_annotations}
                currentAnnotations={state.annotations.length}
              />

              {state.annotations.length >= exercise.min_annotations && (
                <button
                  onClick={handleSubmit}
                  disabled={!isComplete || state.saving}
                  className="mt-4 w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed py-3 rounded-lg font-medium transition-colors"
                >
                  {state.saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Submit Analysis
                </button>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-900/20 border border-green-800 rounded-lg p-4">
                <h3 className="font-medium text-green-300">Analysis Submitted!</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Now explore feedback options below.
                </p>
              </div>

              {/* Feedback Options */}
              <div className="space-y-3">
                {!state.aiFeedback && (
                  <button
                    onClick={handleGetFeedback}
                    disabled={state.loadingFeedback}
                    className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 py-3 rounded-lg font-medium transition-colors"
                  >
                    {state.loadingFeedback ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    Get AI Feedback
                  </button>
                )}

                {state.aiFeedback && (
                  <div className="bg-purple-900/20 border border-purple-800 rounded-lg p-4">
                    <div className="flex items-start gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                      <div>
                        <h4 className="font-medium text-purple-300">AI Feedback</h4>
                        <p className="text-xs text-gray-500 mt-1">
                          Consider: Do you agree with this AI's assessment? Apply your Close Reading skills here too.
                        </p>
                      </div>
                    </div>

                    {state.aiFeedback.strengths && (
                      <div className="mb-3">
                        <h5 className="text-sm font-medium text-gray-300">Strengths</h5>
                        <ul className="text-sm text-gray-400 list-disc list-inside">
                          {state.aiFeedback.strengths.map((s: string, i: number) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {state.aiFeedback.missedPatterns && state.aiFeedback.missedPatterns.length > 0 && (
                      <div className="mb-3">
                        <h5 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                          Patterns to Consider
                          <span className="text-xs text-gray-500 font-normal">Click to add as annotation</span>
                        </h5>
                        <div className="space-y-2 mt-2">
                          {state.aiFeedback.missedPatterns.map((p: { passage: string; suggestedCategory: string; why: string }, i: number) => {
                            const isAdded = state.addedFromFeedback.includes(i);
                            return (
                              <div key={i} className={`rounded p-2 ${isAdded ? 'bg-green-950/30 border border-green-800/50' : 'bg-purple-950/30'}`}>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <p className="text-xs text-purple-300 mb-1">
                                      <span className="font-medium">{p.suggestedCategory}</span>
                                    </p>
                                    <p className="text-sm text-gray-300 italic">"{p.passage}"</p>
                                    <p className="text-xs text-gray-400 mt-1">{p.why}</p>
                                  </div>
                                  {!isAdded ? (
                                    <button
                                      onClick={() => handleAddFromFeedback(i, p.passage, p.suggestedCategory)}
                                      className="shrink-0 p-1.5 rounded bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 transition-colors"
                                      title="Add as annotation"
                                    >
                                      <PlusCircle className="w-4 h-4" />
                                    </button>
                                  ) : (
                                    <span className="shrink-0 text-xs text-green-400 px-2 py-1">Added!</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {state.aiFeedback.questions && state.aiFeedback.questions.length > 0 && (
                      <div className="mb-3">
                        <h5 className="text-sm font-medium text-gray-300">Questions to Explore</h5>
                        <ul className="text-sm text-gray-400 list-disc list-inside mt-1">
                          {state.aiFeedback.questions.map((q: string, i: number) => (
                            <li key={i}>{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {state.aiFeedback.summary && (
                      <p className="text-sm text-gray-300 italic border-t border-purple-800/50 pt-3 mt-3">{state.aiFeedback.summary}</p>
                    )}

                    {/* Refine Analysis Button */}
                    {state.phase === 'feedback' && (
                      <button
                        onClick={handleStartRefining}
                        className="mt-4 w-full flex items-center justify-center gap-2 bg-purple-700 hover:bg-purple-600 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Refine Your Analysis
                      </button>
                    )}
                  </div>
                )}

                {/* Refining mode - show analysis form */}
                {state.phase === 'refining' && (
                  <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
                    <h4 className="font-medium text-blue-300 mb-3 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4" />
                      Refining Analysis
                    </h4>
                    <p className="text-sm text-gray-400 mb-4">
                      Add more annotations and update your analysis based on the feedback.
                    </p>
                    <AnalysisForm
                      claims={state.claims}
                      argument={state.argument}
                      theory={state.theory}
                      onClaimsChange={(v) => dispatch({ type: 'SET_CLAIMS', payload: v })}
                      onArgumentChange={(v) => dispatch({ type: 'SET_ARGUMENT', payload: v })}
                      onTheoryChange={(v) => dispatch({ type: 'SET_THEORY', payload: v })}
                      minAnnotations={exercise.min_annotations}
                      currentAnnotations={state.annotations.length}
                    />
                    <button
                      onClick={handleSubmit}
                      disabled={!isComplete || state.saving}
                      className="mt-4 w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed py-3 rounded-lg font-medium transition-colors"
                    >
                      {state.saving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Resubmit Analysis
                    </button>
                  </div>
                )}

                {!state.exemplar && (
                  <button
                    onClick={handleGetExemplar}
                    className="w-full flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 py-3 rounded-lg font-medium transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    View Expert Analysis
                  </button>
                )}

                {state.exemplar && (
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Eye className="w-4 h-4 text-gray-400" />
                      <h4 className="font-medium text-gray-300">Expert Analysis</h4>
                    </div>

                    {/* Expert Annotations Summary */}
                    {state.exemplar.annotations && state.exemplar.annotations.length > 0 && (
                      <div className="mb-4 p-3 bg-gray-850 rounded-lg">
                        <h5 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                          Expert Annotations ({state.exemplar.annotations.length})
                        </h5>
                        <div className="space-y-1">
                          {state.exemplar.annotations.slice(0, 5).map((ann: Annotation, i: number) => (
                            <div key={i} className="text-xs text-gray-500">
                              <span className="text-gray-400">{ann.categories.join(', ')}</span>
                              {ann.note && <span className="ml-2 italic">"...{ann.note.slice(0, 50)}"</span>}
                            </div>
                          ))}
                          {state.exemplar.annotations.length > 5 && (
                            <p className="text-xs text-gray-600">+{state.exemplar.annotations.length - 5} more</p>
                          )}
                        </div>
                      </div>
                    )}

                    {state.exemplar.analysis && (
                      <div className="space-y-3 text-sm">
                        <div>
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Local Claims</span>
                          <p className="text-gray-400 mt-1">{state.exemplar.analysis.claims}</p>
                        </div>
                        <div>
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Regional Argument</span>
                          <p className="text-gray-400 mt-1">{state.exemplar.analysis.argument}</p>
                        </div>
                        <div>
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Global Theory</span>
                          <p className="text-gray-400 mt-1">{state.exemplar.analysis.theory}</p>
                        </div>
                      </div>
                    )}

                    {!state.exemplar.analysis && !state.exemplar.annotations && (
                      <p className="text-sm text-gray-500 italic">
                        Expert analysis not yet available for this exercise.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CloseReadingExercise;
